import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useCartStore } from '../store/index'
import { supabase } from '../lib/supabaseClient'
import Header from '../components/Header'
import Footer from '../components/Footer'

const ProductsPage = () => {
  const { addItem } = useCartStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'website', name: 'Websites' },
    { id: 'mobile', name: 'Mobile Apps' },
    { id: 'design', name: 'UI/UX Design' },
    { id: 'marketing', name: 'Digital Marketing' },
    { id: 'plugin', name: 'Plugins' },
    { id: 'api', name: 'APIs' }
  ]

  useEffect(() => {
    fetchProducts()
  }, [])

  const fetchProducts = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (error) throw error
      setProducts(data || [])
    } catch (err) {
      console.error('Error fetching products:', err)
      setError('Failed to load products')
    } finally {
      setLoading(false)
    }
  }

  const normalizeDriveUrl = (url) => {
    try {
      const u = new URL(url)
      if (u.hostname.includes('drive.google.com')) {
        // formats: /file/d/ID/view or open?id=ID
        const m = u.pathname.match(/\/file\/d\/([^/]+)/)
        const id = m ? m[1] : u.searchParams.get('id')
        if (id) return `https://drive.google.com/uc?export=view&id=${id}`
      }
      return url
    } catch { return url }
  }

  const toWeservProxy = (url) => {
    try {
      const u = new URL(url)
      return `https://images.weserv.nl/?url=${u.hostname}${u.pathname}${u.search}`
    } catch { return url }
  }

  const getDisplayImageUrl = (raw) => {
    if (!raw) return 'https://via.placeholder.com/400x250?text=No+Image'
    const cleaned = normalizeDriveUrl(raw)
    return cleaned
  }

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="page-container py-20">
          <div className="flex items-center justify-center">
            <div className="loading-spinner"></div>
            <span className="ml-2">Loading products...</span>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="page-container py-20">
          <div className="text-center">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Products</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={fetchProducts}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="page-container text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Our Products & Services
          </h1>
          <p className="text-xl max-w-3xl mx-auto opacity-90">
            Choose from our range of digital products and services designed to help your business grow.
          </p>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 bg-gray-50">
        <div className="page-container">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field"
              />
            </div>
            <div className="md:w-64">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="input-field"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-white">
        <div className="page-container">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📦</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">No Products Found</h2>
              <p className="text-gray-600 mb-6">
                {searchTerm || selectedCategory !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No products are currently available. Check back later!'
                }
              </p>
              {(searchTerm || selectedCategory !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('')
                    setSelectedCategory('all')
                  }}
                  className="btn-primary"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid-responsive">
              {filteredProducts.map((product) => (
                <div key={product.id} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 overflow-hidden flex flex-col h-full">
                  {/* Image Section */}
                  <div className="relative overflow-hidden">
                    <img
                      src={getDisplayImageUrl(product.image_url)}
                      alt={product.name}
                      className="w-full h-56 object-cover transition-transform duration-300 hover:scale-105"
                      
                      onError={(e) => {
                        const tried = e.currentTarget.getAttribute('data-proxy-tried')
                        const src = e.currentTarget.getAttribute('src')
                        if (!tried && src && !src.includes('images.weserv.nl')) {
                          e.currentTarget.setAttribute('data-proxy-tried', '1')
                          e.currentTarget.src = toWeservProxy(src)
                        } else {
                          e.currentTarget.src = 'https://via.placeholder.com/400x250?text=No+Image'
                        }
                      }}
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full shadow-md ${
                        product.type === 'service' ? 'bg-blue-500 text-white' :
                        product.type === 'template' ? 'bg-green-500 text-white' :
                        product.type === 'plugin' ? 'bg-purple-500 text-white' :
                        'bg-gray-500 text-white'
                      }`}>
                        {product.type?.charAt(0).toUpperCase() + product.type?.slice(1) || 'Product'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content Section */}
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-xl font-bold mb-3 text-gray-900 line-clamp-2">{product.name}</h3>
                    <p className="text-gray-600 mb-4 text-sm line-clamp-3 leading-relaxed flex-grow">{product.description}</p>
                    
                    {/* Features */}
                    {product.features && product.features.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Features:</h4>
                        <ul className="space-y-1">
                          {product.features.slice(0, 3).map((feature, index) => (
                            <li key={index} className="flex items-center text-sm text-gray-600">
                              <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span className="line-clamp-1">{feature}</span>
                            </li>
                          ))}
                          {product.features.length > 3 && (
                            <li className="text-xs text-gray-500 ml-6">+{product.features.length - 3} more features</li>
                          )}
                        </ul>
                      </div>
                    )}
                    
                    {/* Price */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="text-2xl font-bold text-primary-600">
                        ৳{product.price?.toLocaleString() || 'Contact for Price'}
                      </div>
                    </div>
                    
                    {/* Action Buttons - Always at bottom */}
                    <div className="flex gap-3 mt-auto">
                      <Link 
                        to={`/products/${product.id}`}
                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg text-center transition-colors duration-200"
                      >
                        View Details
                      </Link>
                      <button 
                        onClick={() => addItem(product)}
                        className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105"
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-gray-50">
        <div className="page-container">
          <h2 className="section-title">Product Categories</h2>
          <p className="section-subtitle">
            Browse our products by category
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="card text-center hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Services</h3>
              <p className="text-gray-600 text-sm">Custom development and consulting services</p>
            </div>
            
            <div className="card text-center hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Templates</h3>
              <p className="text-gray-600 text-sm">Ready-to-use website templates</p>
            </div>
            
            <div className="card text-center hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Plugins</h3>
              <p className="text-gray-600 text-sm">WordPress and other platform plugins</p>
            </div>
            
            <div className="card text-center hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Software</h3>
              <p className="text-gray-600 text-sm">Desktop and web applications</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default ProductsPage